import axios, { AxiosError } from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { getConfig } from "../config/apiConfig";
import { MDPartyDetail, createEmptyMDPartyDetail } from "../data/MDPartyDetail";
import { OptionDTO } from "../data/OptionDTO";
import NexratesCounterpartiesModalData from "./NexratesCounterpartiesModalData";
import { useAuth } from "react-oidc-context";


export enum ModalMode {NONE = 'NONE', CREATE = 'CREATE', EDIT = 'EDIT', VIEW = 'VIEW'}

const NexratesCounterpartiesModal : React.FC<{
    partyCode: string | undefined,
    onCloseModal: (refresh: boolean) => void,
    modalMode: ModalMode.EDIT | ModalMode.VIEW | ModalMode.CREATE
}> = ({partyCode, onCloseModal, modalMode}) => {

    const API_BASE_URL = getConfig().REACT_APP_API_BASE_URL;

    const [sites, setSites] = useState<OptionDTO[]>([]);
    const [tierLevels, setTierLevels] = useState<OptionDTO[]>([]);
    const [data, setData] = useState<MDPartyDetail>(createEmptyMDPartyDetail());

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);

    const auth = useAuth();
    const token = auth.user?.access_token;

    console.log("Re-rendering NexratesCounterpartiesModal");

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const responseSites = await axios.get(`${API_BASE_URL}/parties/sites`, {headers: {Authorization: `Bearer ${token}`}});
                let sitesData : OptionDTO[];
                if ((responseSites.data as OptionDTO[]).length > 1) {
                    sitesData = [{label: " ", value: undefined}, ...responseSites.data as OptionDTO[]];
                } else {
                    sitesData = responseSites.data;
                }
                setSites(sitesData);
                const responseTiers = await axios.get(`${API_BASE_URL}/parties/tierlevels`, {headers: {Authorization: `Bearer ${token}`}});
                setTierLevels(responseTiers.data);
                if (partyCode !== undefined) {
                    const responseParties = await axios.get(`${API_BASE_URL}/parties/${partyCode}`, {headers: {Authorization: `Bearer ${token}`}});
                    setData(responseParties.data);
                } else {
                    const detail : MDPartyDetail = createEmptyMDPartyDetail();
                    if (sitesData.length > 0) {
                        detail.siteCode = sitesData[0].value;
                    }
                    detail.bondTierId = "2";
                    detail.irsTierId = "2";
                    setData(detail);
                }
                setIsError(false);
            } catch {
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [partyCode, API_BASE_URL, token]);

    const handleFormChange = useCallback(<K extends keyof MDPartyDetail>(fieldName : K, newValue : string) => {
        setData(oldData => ({...oldData, [fieldName]: newValue}));
    }, []);

    const handleSave = () => {
        const saveData = async (dto : MDPartyDetail) => {
            try {
                await axios.post(`${API_BASE_URL}/parties`, dto, {headers: {Authorization: `Bearer ${token}`}});
                alert("Saved!")
                onCloseModal(true);
            } catch (err: any) {
                const axiosError = err as AxiosError;
                alert("Error while saving! " + axiosError.response?.data + ".");
            } finally {
                setIsLoading(false);
            }
        };
        saveData(data);        
        console.log("Going to save ..." + JSON.stringify(data));
    };

    const handleEdit = () => {
        const saveData = async (dto : MDPartyDetail) => {
            try {
                await axios.put(`${API_BASE_URL}/parties`, dto, {headers: {Authorization: `Bearer ${token}`}});
                alert("Saved!")
                onCloseModal(true);
            } catch (err: any) {
                const axiosError = err as AxiosError;
                alert("Error while saving! " + axiosError.response?.data + ".");
            } finally {
                setIsLoading(false);
            }
        };
        saveData(data);        
        console.log("Going to save ..." + JSON.stringify(data));
    };    

    return (<>
        <div className="modal d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Counterparty {modalMode === ModalMode.VIEW ? 'Details' : 
                                                                  modalMode === ModalMode.EDIT ? 'Edition' : 'Creation'}</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={() => onCloseModal(false)}></button>
                    </div>
                    <div className="modal-body">
                        {isLoading && <p>Loading...</p>}
                        {isError && <p>Error!</p>}
                        {!isLoading && !isError && data !== undefined && 
                        <NexratesCounterpartiesModalData formData={data} modalMode={modalMode} sites={sites} tierLevels={tierLevels} onFormChange={handleFormChange} />}
                    </div>
                    <div className="modal-footer">
                        {modalMode === ModalMode.CREATE && <button type="button" className="btn btn-success" onClick={() => handleSave()}>
                            Create
                        </button>}
                        {modalMode === ModalMode.EDIT && <button type="button" className="btn btn-success" onClick={() => handleEdit()}>
                            Save
                        </button>}
                        <button type="button" className="btn btn-secondary" onClick={() => onCloseModal(false)}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div className="modal-backdrop show" onClick={() => onCloseModal(false)}></div>
    </>);
};

export default NexratesCounterpartiesModal;